import React, { useState, useMemo } from "react";
import { Card, DatePicker, Typography, Grid, Modal, Tag } from "antd";
import dayjs from "dayjs";
import styled from "styled-components";
import {
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { getUserInfo } from "redux/selectors";
import { connect, useSelector } from "react-redux";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;



const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const EngagementCalendar = ({ selectMonthYear, setSelectMonthYear }) => {
  const userInfo = useSelector(getUserInfo);
  const selectedYear = selectMonthYear?.year || dayjs().year();
  const selectedMonth = selectMonthYear?.month || months[dayjs().month()];
  const monthIndex = months.indexOf(selectedMonth);

  const [selectedEngagement, setSelectedEngagement] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const startDate = dayjs(
    `${selectedYear}-${String(monthIndex + 1).padStart(2, "0")}-01`
  );

  const totalDays = startDate.daysInMonth();
  const startDatePayload = dayjs(
    `${selectedYear}-${String(monthIndex + 1).padStart(2, "0")}-01`
  ).format("DD-MM-YYYY");

  const endDatePayload = dayjs(
    `${selectedYear}-${String(monthIndex + 1).padStart(2, "0")}-01`
  )
    .endOf("month")
    .format("DD-MM-YYYY");

  const screens = useBreakpoint();
  const { key: engagementKey, url: engagementUrl } =
    queryKeys.getDashboardEngagement();

  const engagementPayload = {
    userid: userInfo?.userid,
    startdate: startDatePayload,
    enddate: endDatePayload,
    managingOffice: userInfo?.managingoffice?.[0] || "Zambia",
  };

  const { data: engagementDataRes } = useFetch(
    engagementKey,
    engagementUrl,
    {},
    engagementPayload
  );

  const engagementData = useMemo(() => {
    return engagementDataRes?.response?.data || [];
  }, [engagementDataRes]);

  const isMobile = !screens.md;
  const cellWidth = isMobile ? 60 : 80;
  const rowHeight = isMobile ? 42 : 50;

  const daysArray = useMemo(
    () =>
      Array.from({ length: totalDays }, (_, i) =>
        startDate.add(i, "day").format("DD")
      ),
    [totalDays, startDate]
  );

  const getBarStyle = (item) => {
    if (!item.actualBookedStartDate || !item.actualBookedEndDate) return null;

    const start = dayjs(item.actualBookedStartDate);
    const end = dayjs(item.actualBookedEndDate);

    const monthStart = startDate.startOf("month");
    const monthEnd = startDate.endOf("month");

    if (end.isBefore(monthStart) || start.isAfter(monthEnd)) return null;

    const effectiveStart = start.isBefore(monthStart) ? monthStart : start;
    const effectiveEnd = end.isAfter(monthEnd) ? monthEnd : end;

    const leftOffset = effectiveStart.date() - 1;
    const duration = effectiveEnd.diff(effectiveStart, "day") + 1;

    return {
      position: "absolute",
      left: leftOffset * cellWidth,
      width: duration * cellWidth,
      height: rowHeight - 6,
      background: "#C2FAE5",
      borderRadius: 6,
      paddingLeft: 8,
      color: "black",
      fontSize: isMobile ? 11 : 13,
      display: "flex",
      alignItems: "center",
      whiteSpace: "nowrap",
      cursor: "pointer",         // 👈 pointer cursor
    };
  };

  const handleBarClick = (item) => {
    setSelectedEngagement(item);
    setModalVisible(true);
  };

  const [currentMonthIndex, setCurrentMonthIndex] = useState(monthIndex);

  const goPrev = () => {
    if (currentMonthIndex > 0) {
      setCurrentMonthIndex((v) => v - 1);
      setSelectMonthYear((p) => ({ ...p, month: months[currentMonthIndex - 1] }));
    }
  };

  const goNext = () => {
    if (currentMonthIndex < 11) {
      setCurrentMonthIndex((v) => v + 1);
      setSelectMonthYear((p) => ({ ...p, month: months[currentMonthIndex + 1] }));
    }
  };

  const totalRows = Math.max(engagementData.length, 5);

  // Helper to format date nicely
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return dayjs(dateStr).format("DD MMM YYYY");
  };

  return (
    <Card bordered={false} style={{ borderRadius: 12 }}>
      {/* Header */}
      <Header>
        <Title level={5} style={{ margin: 0 }}>Engagement</Title>
        <Controls>
          <DatePicker
            picker="year"
            value={dayjs().year(selectedYear)}
            onChange={(date) =>
              setSelectMonthYear((p) => ({ ...p, year: date?.year() }))
            }
          />
          <MonthNav>
            <LeftOutlined onClick={goPrev} />
            <span><CalendarOutlined /> {months[currentMonthIndex]}</span>
            <RightOutlined onClick={goNext} />
          </MonthNav>
        </Controls>
      </Header>

      {/* Calendar */}
      <ScrollWrap>
        <GridWrap
          style={{
            gridTemplateColumns: `repeat(${totalDays}, ${cellWidth}px)`,
            gridTemplateRows: `50px repeat(${totalRows}, ${rowHeight}px)`,
            minWidth: totalDays * cellWidth,
          }}
        >
          {daysArray.map((d, i) => <Cell key={i} header>{d}</Cell>)}
          {Array.from({ length: totalRows * totalDays }).map((_, i) => <Cell key={i} />)}

          {engagementData.map((item, i) => {
            const style = getBarStyle(item);
            if (!style) return null;
            return (
              <BarRow key={`${item.clientId}-${i}`} style={{ top: 50 + i * rowHeight }}>
                {/* 👇 pointer-events: auto so click works */}
                <div
                  style={{ ...style, pointerEvents: "auto" }}
                  onClick={() => handleBarClick(item)}
                >
                  {item.clientName}
                </div>
              </BarRow>
            );
          })}
        </GridWrap>
      </ScrollWrap>

      {/* ===== Engagement Details Modal ===== */}
      <Modal
        title={null}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={520}
        closable={false}
        styles={{ body: { padding: 0 } }}
		style={{ borderRadius: 12 }}
      >
        {selectedEngagement && (
          <ModalContainer>
            {/* Header */}
            <ModalHeader>
              <ModalTitle>Engagement Details</ModalTitle>
              <HeaderRight>
                <ProgressPill>
                  Progress&nbsp;&nbsp;
                  <ProgressValue>
                    {selectedEngagement.personDays
                      ? `${Math.round((selectedEngagement.personDays / 42) * 100)}%`
                      : "75%"}
                  </ProgressValue>
                </ProgressPill>
                <CloseBtn onClick={() => setModalVisible(false)}>✕</CloseBtn>
              </HeaderRight>
            </ModalHeader>

            {/* Rows */}
            <ModalBody>
              <ModalRow>
                <ModalLabel>Client</ModalLabel>
                <ModalValue>{selectedEngagement.clientName || "N/A"}</ModalValue>
              </ModalRow>
              <ModalRow>
                <ModalLabel>Person In-Charge</ModalLabel>
                <ModalValue>{selectedEngagement.personIncharge || "N/A"}</ModalValue>
              </ModalRow>
              <ModalRow>
                <ModalLabel>Partner</ModalLabel>
                <ModalValue>{selectedEngagement.partner || "N/A"}</ModalValue>
              </ModalRow>
              <ModalRow>
                <ModalLabel>Other Members</ModalLabel>
                <ModalValue>{selectedEngagement.userId || "N/A"}</ModalValue>
              </ModalRow>
              <ModalRow>
                <ModalLabel>Start Date</ModalLabel>
                <ModalValue>
                  {selectedEngagement.actualBookedStartDate
                    ? <>
                        {dayjs(selectedEngagement.actualBookedStartDate).format("DD MMM YYYY")}
                        {" "}&ndash;{" "}
                        <em>{dayjs(selectedEngagement.actualBookedStartDate).format("ddd")}</em>
                      </>
                    : "N/A"}
                </ModalValue>
              </ModalRow>
              <ModalRow>
                <ModalLabel>End Date</ModalLabel>
                <ModalValue>
                  {selectedEngagement.actualBookedEndDate
                    ? <>
                        {dayjs(selectedEngagement.actualBookedEndDate).format("DD MMM YYYY")}
                        {" "}&ndash;{" "}
                        <em>{dayjs(selectedEngagement.actualBookedEndDate).format("ddd")}</em>
                      </>
                    : "N/A"}
                </ModalValue>
              </ModalRow>
              <ModalRow>
                <ModalLabel>Total Booked Hours</ModalLabel>
                <ModalValue>{selectedEngagement.personDays ? selectedEngagement.personDays * 8 : "N/A"}</ModalValue>
              </ModalRow>
              <ModalRow last>
                <ModalLabel>Hours Worked</ModalLabel>
                <ModalValue>{selectedEngagement.personDays || "N/A"}</ModalValue>
              </ModalRow>
            </ModalBody>
          </ModalContainer>
        )}
      </Modal>
    </Card>
  );
};

export default EngagementCalendar;

/* ===================== STYLES ===================== */

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const Controls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const MonthNav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #0066ff;
`;

const ScrollWrap = styled.div`
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const GridWrap = styled.div`
  display: grid;
  position: relative;
`;

const Cell = styled.div`
  border-right: 1px solid #eee;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  background: ${(p) => (p.header ? "#fafafa" : "#fff")};
  font-weight: ${(p) => (p.header ? 600 : 400)};
  position: ${(p) => (p.header ? "sticky" : "static")};
  top: ${(p) => (p.header ? 0 : "auto")};
  z-index: ${(p) => (p.header ? 2 : 1)};
`;

const BarRow = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  z-index: 3;
  pointer-events: none;   /* row itself ignores clicks */
`;

const ModalContainer = styled.div`
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
`;

const ModalTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ProgressPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #d1fae5;
  border-radius: 999px;
  padding: 3px 12px;
  font-size: 13px;
  color: #065f46;
  font-weight: 500;
`;

const ProgressValue = styled.span`
  font-weight: 700;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 15px;
  cursor: pointer;
  color: #9ca3af;
  padding: 0;
  line-height: 1;
  &:hover { color: #111; }
`;

const ModalBody = styled.div`
  padding: 0 20px 12px 20px;
`;

const ModalRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  align-items: center;
  padding: 10px 0;
  border-bottom: ${(p) => (p.last ? "none" : "1px solid #f3f4f6")};
`;

const ModalLabel = styled.span`
  color: #9ca3af;
  font-size: 13px;
`;

const ModalValue = styled.span`
  color: #111827;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
`;